import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { DEFAULT_CONTRACT_TEMPLATE_HTML } from '../constants/default-contract-template';
import { PlatformBillingSettings } from '../models/billing.model';
import { parsePlatformBilling } from '../shared/billing/platform-billing.utils';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';

export interface GlobalSettingsDocument {
  defaultContractTemplateHtml?: string;
  maintenanceMode?: boolean;
  platformBilling?: PlatformBillingSettings;
}

@Injectable({ providedIn: 'root' })
export class GlobalSettingsService extends FirebaseAwareService {
  private firestore = inject(Firestore);

  private settingsRef() {
    return this.inCtx(() => doc(this.firestore, 'config', 'globalSettings'));
  }

  private legacyRef() {
    return this.inCtx(() => doc(this.firestore, 'platformConfig', 'defaultLeaseTemplate'));
  }

  private readTemplate(data: Record<string, unknown> | undefined): string | null {
    if (!data) return null;
    const value = typeof data['defaultContractTemplateHtml'] === 'string'
      ? data['defaultContractTemplateHtml']
      : typeof data['templateHtml'] === 'string'
        ? data['templateHtml']
        : '';
    return value.trim() ? value : null;
  }

  async loadDefaultContractTemplateHtml(): Promise<string> {
    const currentSnap = await this.inCtx(() => getDoc(this.settingsRef()));
    const current = this.readTemplate(currentSnap.exists() ? (currentSnap.data() as Record<string, unknown>) : undefined);
    if (current) return current;

    const legacySnap = await this.inCtx(() => getDoc(this.legacyRef()));
    const legacy = this.readTemplate(legacySnap.exists() ? (legacySnap.data() as Record<string, unknown>) : undefined);
    return legacy ?? DEFAULT_CONTRACT_TEMPLATE_HTML;
  }

  async saveDefaultContractTemplateHtml(html: string): Promise<void> {
    await this.inCtx(() => setDoc(this.settingsRef(), {
      defaultContractTemplateHtml: html,
    }, { merge: true }));
  }

  async loadMaintenanceMode(): Promise<boolean> {
    const snap = await this.inCtx(() => getDoc(this.settingsRef()));
    if (!snap.exists()) return false;
    const data = snap.data() as GlobalSettingsDocument;
    return data.maintenanceMode === true;
  }

  async saveMaintenanceMode(enabled: boolean): Promise<void> {
    await this.inCtx(() => setDoc(this.settingsRef(), { maintenanceMode: enabled }, { merge: true }));
  }

  async loadPlatformBilling(): Promise<PlatformBillingSettings | null> {
    const snap = await this.inCtx(() => getDoc(this.settingsRef()));
    if (!snap.exists()) return null;
    const data = snap.data() as GlobalSettingsDocument;
    return parsePlatformBilling(data.platformBilling);
  }

  async savePlatformBilling(settings: PlatformBillingSettings): Promise<void> {
    await this.inCtx(() => setDoc(this.settingsRef(), {
      platformBilling: {
        ...settings,
        taxId: settings.taxId.replace(/[\s.-]/g, '').toUpperCase(),
        updatedAt: Date.now(),
      },
    }, { merge: true }));
  }

}
